import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getProspectoDetalle, createSeguimiento, createCita, updateEstatusCita } from '../services/api';

const ESTATUS_COLORS: Record<string, string> = {
  Nuevo:      'bg-gray-100 text-gray-700',
  Contactado: 'bg-blue-100 text-blue-700',
  Interesado: 'bg-yellow-100 text-yellow-700',
  Cotizado:   'bg-purple-100 text-purple-700',
  Convertido: 'bg-green-100 text-green-700',
  Perdido:    'bg-red-100 text-red-700',
};

const TIPO_SEGUIMIENTO = ['Llamada', 'WhatsApp', 'Email', 'Visita'];

const EMPTY_SEG_FORM = {
  tipo: 'Llamada', resultado: '', notas: '', proximoContacto: '',
  consiguioCita: false, citaTitulo: '', citaFechaHora: '', citaTipo: 'PrimerContacto',
};
const EMPTY_CITA_FORM = { titulo: '', fechaHora: '', tipo: 'Seguimiento', notas: '', lugar: '' };
const TIPOS_CITA = ['PrimerContacto','Seguimiento','Cierre','Otro'];

export default function ProspectoDetalle() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [prospecto, setProspecto] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [showSegForm, setShowSegForm] = useState(false);
  const [segForm, setSegForm] = useState(EMPTY_SEG_FORM);
  const [savingSeg, setSavingSeg] = useState(false);
  const [segError, setSegError] = useState('');
  const [showCitaForm, setShowCitaForm] = useState(false);
  const [citaForm, setCitaForm] = useState(EMPTY_CITA_FORM);
  const [savingCita, setSavingCita] = useState(false);
  const [citaError, setCitaError] = useState('');

  const load = () =>
    getProspectoDetalle(Number(id))
      .then(res => setProspecto(res.data))
      .catch(() => navigate('/prospectos'))
      .finally(() => setLoading(false));

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleRegistrarSeguimiento = async (e: React.FormEvent) => {
    e.preventDefault();
    setSavingSeg(true);
    setSegError('');
    try {
      await createSeguimiento({
        tipo: segForm.tipo,
        resultado: segForm.resultado,
        notas: segForm.notas || null,
        proximoContacto: segForm.proximoContacto || null,
        prospectoId: Number(id),
      });
      if (segForm.consiguioCita) {
        await createCita({
          titulo: segForm.citaTitulo || `Cita con ${prospecto?.nombre ?? ''}`,
          fechaHora: segForm.citaFechaHora,
          duracionMinutos: 60,
          tipo: segForm.citaTipo,
          prospectoId: Number(id),
          todoElDia: false,
        });
      }
      setSegForm(EMPTY_SEG_FORM);
      setShowSegForm(false);
      load();
    } catch {
      setSegError('Error al registrar la llamada.');
    } finally {
      setSavingSeg(false);
    }
  };

  const handleEstatusCita = async (citaId: number, estatus: string) => {
    await updateEstatusCita(citaId, estatus);
    load();
  };

  const handleAgregarCita = async (e: React.FormEvent) => {
    e.preventDefault();
    setSavingCita(true);
    setCitaError('');
    try {
      await createCita({
        titulo: citaForm.titulo,
        fechaHora: citaForm.fechaHora,
        duracionMinutos: 60,
        tipo: citaForm.tipo,
        notas: citaForm.notas || null,
        lugar: citaForm.lugar || null,
        prospectoId: Number(id),
        todoElDia: false,
      });
      setCitaForm(EMPTY_CITA_FORM);
      setShowCitaForm(false);
      load();
    } catch {
      setCitaError('Error al agendar la cita.');
    } finally {
      setSavingCita(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64 text-gray-400">
        Cargando...
      </div>
    );
  }

  if (!prospecto) return null;

  const seguimientos = prospecto.seguimientos ?? [];
  const citas = prospecto.citas ?? [];
  const cotizaciones = prospecto.cotizaciones ?? [];

  const proxContacto = prospecto.fechaProximoContacto
    ? new Date(prospecto.fechaProximoContacto)
    : null;
  const proxContactoVencida = proxContacto && proxContacto < new Date();

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Encabezado */}
      <div className="flex items-center gap-3">
        <button
          onClick={() => navigate('/prospectos')}
          className="text-gray-400 hover:text-gray-600 text-xl leading-none"
        >
          ←
        </button>
        <div className="flex-1">
          <div className="flex items-center gap-3 flex-wrap">
            <h2 className="text-2xl font-bold text-gray-800">{prospecto.nombre}</h2>
            <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${ESTATUS_COLORS[prospecto.estatus] ?? 'bg-gray-100 text-gray-700'}`}>
              {prospecto.estatus}
            </span>
            {proxContacto && (
              <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${proxContactoVencida ? 'bg-red-100 text-red-700' : 'bg-blue-50 text-blue-700'}`}>
                Próximo: {proxContacto.toLocaleDateString('es-MX')}
              </span>
            )}
          </div>
          <p className="text-sm text-gray-500 mt-0.5">{prospecto.telefono}{prospecto.email ? ` · ${prospecto.email}` : ''}</p>
        </div>
      </div>

      {/* Datos del prospecto */}
      <div className="bg-white rounded-xl shadow-sm p-5">
        <h3 className="font-semibold text-gray-700 mb-4 text-sm uppercase tracking-wider">Información</h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 text-sm">
          {[
            ['Edad', prospecto.edad],
            ['Estado civil', prospecto.estadoCivil],
            ['Hijos', prospecto.numeroHijos],
            ['Ocupación', prospecto.ocupacion],
            ['Ingreso aprox.', prospecto.ingresoAproximado],
            ['Estilo de vida', prospecto.estiloDeVida],
            ['Centro de influencia', prospecto.centroDeInfluencia],
            ['Relación con agente', prospecto.relacionConAgente],
            ['Fuente', prospecto.canalCaptacion],
            ['Producto', prospecto.ramoInteres],
            ['Facilidad cita', prospecto.facilidadCita],
            ['¿Tiene seguro?', prospecto.yaTieneSeguro == null ? null : prospecto.yaTieneSeguro ? 'Sí' : 'No'],
          ].map(([label, value]) =>
            value != null && value !== '' ? (
              <div key={label as string}>
                <p className="text-xs text-gray-400 mb-0.5">{label}</p>
                <p className="text-gray-700">{value}</p>
              </div>
            ) : null
          )}
          {prospecto.comoAbordarlo && (
            <div className="col-span-2 sm:col-span-3">
              <p className="text-xs text-gray-400 mb-0.5">¿Cómo abordarlo?</p>
              <p className="text-gray-700">{prospecto.comoAbordarlo}</p>
            </div>
          )}
          {prospecto.notas && (
            <div className="col-span-2 sm:col-span-3">
              <p className="text-xs text-gray-400 mb-0.5">Notas</p>
              <p className="text-gray-700">{prospecto.notas}</p>
            </div>
          )}
        </div>
      </div>

      {/* Historial de seguimientos */}
      <div className="bg-white rounded-xl shadow-sm p-5">
        <div className="flex justify-between items-center mb-4">
          <h3 className="font-semibold text-gray-700 text-sm uppercase tracking-wider">
            Historial de llamadas / seguimientos
            <span className="ml-2 text-xs font-normal text-gray-400">({seguimientos.length})</span>
          </h3>
          <button
            onClick={() => { setShowSegForm(v => !v); setSegForm(EMPTY_SEG_FORM); setSegError(''); }}
            className="text-sm text-primary-600 hover:text-primary-700 font-medium"
          >
            {showSegForm ? 'Cancelar' : '+ Registrar llamada'}
          </button>
        </div>

        {showSegForm && (
          <form onSubmit={handleRegistrarSeguimiento} className="bg-gray-50 rounded-lg p-4 mb-4 grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Tipo *</label>
              <select
                value={segForm.tipo}
                onChange={e => setSegForm(f => ({ ...f, tipo: e.target.value }))}
                required
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              >
                {TIPO_SEGUIMIENTO.map(t => <option key={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Resultado *</label>
              <input
                type="text"
                value={segForm.resultado}
                onChange={e => setSegForm(f => ({ ...f, resultado: e.target.value }))}
                required
                placeholder="Contestó, no contestó, interesado..."
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              />
            </div>
            <div className="col-span-2">
              <label className="block text-xs font-medium text-gray-600 mb-1">Notas</label>
              <textarea
                value={segForm.notas}
                onChange={e => setSegForm(f => ({ ...f, notas: e.target.value }))}
                rows={2}
                placeholder="Observaciones..."
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm resize-none"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Próximo contacto</label>
              <input
                type="datetime-local"
                value={segForm.proximoContacto}
                onChange={e => setSegForm(f => ({ ...f, proximoContacto: e.target.value }))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              />
            </div>

            <div className="col-span-2 flex items-center gap-2 bg-white rounded-lg border border-gray-200 px-3 py-2">
              <input
                type="checkbox"
                id="consiguioCita"
                checked={segForm.consiguioCita}
                onChange={e => setSegForm(f => ({ ...f, consiguioCita: e.target.checked }))}
                className="h-4 w-4"
              />
              <label htmlFor="consiguioCita" className="text-sm text-gray-700 font-medium">
                ¿Consiguió cita? — agendarla de una vez
              </label>
            </div>

            {segForm.consiguioCita && (
              <>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Fecha y hora de la cita *</label>
                  <input
                    type="datetime-local"
                    value={segForm.citaFechaHora}
                    onChange={e => setSegForm(f => ({ ...f, citaFechaHora: e.target.value }))}
                    required={segForm.consiguioCita}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Tipo de cita *</label>
                  <select
                    value={segForm.citaTipo}
                    onChange={e => setSegForm(f => ({ ...f, citaTipo: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  >
                    {TIPOS_CITA.map(t => <option key={t}>{t}</option>)}
                  </select>
                </div>
              </>
            )}

            {segError && <p className="col-span-2 text-red-500 text-xs">{segError}</p>}
            <div className="col-span-2 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setShowSegForm(false)}
                className="px-3 py-1.5 border rounded-lg text-sm text-gray-600 hover:bg-gray-100"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={savingSeg}
                className="px-3 py-1.5 bg-primary-600 text-white rounded-lg text-sm hover:bg-primary-700 disabled:opacity-60"
              >
                {savingSeg ? 'Guardando...' : 'Registrar'}
              </button>
            </div>
          </form>
        )}

        {seguimientos.length === 0 ? (
          <p className="text-sm text-gray-400 text-center py-4">Sin seguimientos registrados</p>
        ) : (
          <div className="space-y-2">
            {[...seguimientos].sort((a: any, b: any) => new Date(b.fecha).getTime() - new Date(a.fecha).getTime()).map((s: any) => (
              <div key={s.id} className="flex gap-3 border-l-2 border-gray-200 pl-3 py-1">
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs font-semibold text-gray-600">{s.tipo}</span>
                    <span className="text-xs text-gray-500">·</span>
                    <span className="text-xs text-gray-600">{s.resultado}</span>
                    <span className="text-xs text-gray-400 ml-auto">
                      {new Date(s.fecha).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                    </span>
                  </div>
                  {s.notas && <p className="text-xs text-gray-500 mt-0.5">{s.notas}</p>}
                  {s.proximoContacto && (
                    <p className="text-xs text-blue-500 mt-0.5">
                      Próx: {new Date(s.proximoContacto).toLocaleDateString('es-MX')}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Citas */}
      <div className="bg-white rounded-xl shadow-sm p-5">
        <div className="flex justify-between items-center mb-4">
          <h3 className="font-semibold text-gray-700 text-sm uppercase tracking-wider">
            Citas <span className="text-xs font-normal text-gray-400">({citas.length})</span>
          </h3>
          <button
            onClick={() => { setShowCitaForm(v => !v); setCitaForm(EMPTY_CITA_FORM); setCitaError(''); }}
            className="text-sm text-primary-600 hover:text-primary-700 font-medium"
          >
            {showCitaForm ? 'Cancelar' : '+ Agendar cita'}
          </button>
        </div>

        {showCitaForm && (
          <form onSubmit={handleAgregarCita} className="bg-gray-50 rounded-lg p-4 mb-4 grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Título *</label>
              <input
                type="text"
                value={citaForm.titulo}
                onChange={e => setCitaForm(f => ({ ...f, titulo: e.target.value }))}
                required
                placeholder="Nombre de la cita"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Tipo *</label>
              <select
                value={citaForm.tipo}
                onChange={e => setCitaForm(f => ({ ...f, tipo: e.target.value }))}
                required
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              >
                {TIPOS_CITA.map(t => <option key={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Fecha y hora *</label>
              <input
                type="datetime-local"
                value={citaForm.fechaHora}
                onChange={e => setCitaForm(f => ({ ...f, fechaHora: e.target.value }))}
                required
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Lugar</label>
              <input
                type="text"
                value={citaForm.lugar}
                onChange={e => setCitaForm(f => ({ ...f, lugar: e.target.value }))}
                placeholder="Oficina, videollamada..."
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              />
            </div>
            <div className="col-span-2">
              <label className="block text-xs font-medium text-gray-600 mb-1">Notas</label>
              <textarea
                value={citaForm.notas}
                onChange={e => setCitaForm(f => ({ ...f, notas: e.target.value }))}
                rows={2}
                placeholder="Observaciones..."
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm resize-none"
              />
            </div>
            {citaError && <p className="col-span-2 text-red-500 text-xs">{citaError}</p>}
            <div className="col-span-2 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setShowCitaForm(false)}
                className="px-3 py-1.5 border rounded-lg text-sm text-gray-600 hover:bg-gray-100"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={savingCita}
                className="px-3 py-1.5 bg-primary-600 text-white rounded-lg text-sm hover:bg-primary-700 disabled:opacity-60"
              >
                {savingCita ? 'Guardando...' : 'Agendar'}
              </button>
            </div>
          </form>
        )}

        {citas.length === 0 ? (
          <p className="text-sm text-gray-400 text-center py-4">Sin citas agendadas</p>
        ) : (
          <div className="space-y-2">
            {[...citas].sort((a: any, b: any) => new Date(b.fechaHora).getTime() - new Date(a.fechaHora).getTime()).map((c: any) => (
              <div key={c.id} className="flex items-center justify-between text-sm border-b border-gray-50 pb-2">
                <div>
                  <span className="font-medium text-gray-700">{c.titulo}</span>
                  <span className="text-gray-400 ml-2">{c.tipo}</span>
                </div>
                <div className="flex items-center gap-2 text-gray-500">
                  <span>{new Date(c.fechaHora).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
                  {c.estatus === 'Programada' ? (
                    <>
                      <button onClick={() => handleEstatusCita(c.id, 'Completada')}
                        title="Marcar como realizada"
                        className="text-xs px-2 py-0.5 rounded-full bg-green-50 text-green-700 hover:bg-green-100 font-medium">
                        ✓ Se realizó
                      </button>
                      <button onClick={() => handleEstatusCita(c.id, 'NoShow')}
                        title="El prospecto no se presentó"
                        className="text-xs px-2 py-0.5 rounded-full bg-red-50 text-red-700 hover:bg-red-100 font-medium">
                        ✗ No-show
                      </button>
                    </>
                  ) : (
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                      c.estatus === 'Completada' ? 'bg-green-100 text-green-700' :
                      c.estatus === 'NoShow' ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-600'
                    }`}>{c.estatus === 'NoShow' ? 'No-show' : c.estatus}</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Cotizaciones */}
      {cotizaciones.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm p-5">
          <h3 className="font-semibold text-gray-700 text-sm uppercase tracking-wider mb-4">
            Cotizaciones <span className="text-xs font-normal text-gray-400">({cotizaciones.length})</span>
          </h3>
          <div className="space-y-2">
            {cotizaciones.map((c: any) => (
              <div key={c.id} className="flex items-center justify-between text-sm border-b border-gray-50 pb-2">
                <div>
                  <span className="font-medium text-gray-700">{c.ramo}</span>
                  <span className="text-gray-400 ml-2">· {c.aseguradora}</span>
                </div>
                <div className="flex items-center gap-3 text-gray-500">
                  <span className="font-medium text-gray-700">
                    ${Number(c.primaAnual).toLocaleString('es-MX')} / año
                  </span>
                  <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100">{c.estatus}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
